import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { signupCopy, signupFields } from '../../data/signupFields';
import { HONEYPOT_FIELD, useSignupForm } from '../../hooks/useSignupForm';
import { SignupAudience } from '../../types/signup';
interface SignupCardProps {
  audience: SignupAudience;
  onAudienceChange: (a: SignupAudience) => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function SignupCard({
  audience,
  onAudienceChange
}: SignupCardProps) {
  return <div id="signup" className="scroll-mt-24 rounded-3xl border border-line bg-surface p-2 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.2)]" data-id="element-158" data-mp="2khd5j3dd6q5d">
      <div role="tablist" aria-label="Sign up as" className="grid grid-cols-2 gap-1 rounded-2xl bg-canvas p-1" data-id="element-159" data-mp="2fhqqqphw11ug">
        {(['gigs', 'company'] as SignupAudience[]).map(a => <button key={a} role="tab" aria-selected={audience === a} onClick={() => onAudienceChange(a)} className={`relative whitespace-nowrap rounded-xl py-2.5 text-sm font-medium transition-colors duration-150 ${audience === a ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-160" data-mp="3qyi3tmkn3za4">
            {audience === a && <motion.span layoutId="signup-tab" className="absolute inset-0 rounded-xl bg-surface shadow-sm" transition={{
          duration: 0.22,
          ease
        }} data-id="element-161" data-mp="2frqbbzeqkn8o" />}
            <span className="relative" data-id="element-162" data-mp="1o1llhl87j802">{signupCopy[a].tab}</span>
          </button>)}
      </div>

      <AnimatePresence mode="wait" initial={false} data-id="element-163" data-mp="1s3fi9ifa7ruy">
        <motion.div key={audience} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease
      }} data-id="element-164" data-mp="1s1mc048kz9nh">
          <SignupForm audience={audience} data-id="element-165" data-mp="10a6om7z79iw7" />
        </motion.div>
      </AnimatePresence>
    </div>;
}
function SignupForm({
  audience
}: {
  audience: SignupAudience;
}) {
  const fields = signupFields[audience];
  const copy = signupCopy[audience];
  const {
    values,
    errors,
    status,
    handleChange,
    handleSubmit,
    reset
  } = useSignupForm(fields, audience);
  const firstName = (values.fullName ?? '').trim().split(' ')[0] || 'friend';
  if (status === 'success') {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.25,
      ease
    }} className="flex min-h-[340px] flex-col items-center justify-center px-6 py-10 text-center" role="status" data-id="element-166" data-mp="2cg0jzuq9uhul">
        <motion.span initial={{
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 20,
        delay: 0.05
      }} className="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-ink" data-id="element-167" data-mp="1xr40n1irlieo">
          <CheckIcon className="h-7 w-7" strokeWidth={3} aria-hidden="true" data-id="element-168" data-mp="353csx23ier5f" />
        </motion.span>
        <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight" data-id="element-169" data-mp="3clualvljhc9h">You're on the list.</h3>
        <p className="mt-2 max-w-xs text-muted" data-id="element-170" data-mp="3hntc0zxpxz3s">{copy.success(firstName, values)}</p>
        <button onClick={reset} className="mt-6 text-sm font-medium text-accent underline-offset-4 hover:underline" data-id="element-171" data-mp="32qj0xrozmv8l">
          Sign up someone else
        </button>
      </motion.div>;
  }
  const submitting = status === 'submitting';
  return <form onSubmit={handleSubmit} noValidate className="p-4 pt-5 md:p-6" data-id="element-172" data-mp="47c6u62msivo">
      <h2 className="font-display text-2xl font-semibold tracking-tight" data-id="element-173" data-mp="49ojq4tj831v">{copy.title}</h2>

      <div className={`mt-5 grid gap-4 ${audience === 'company' ? 'sm:grid-cols-2' : ''}`} data-id="element-174" data-mp="3lntt6sc7coej">
        {fields.map((f, i) => {
        const id = `${audience}-${f.name}`;
        const error = errors[f.name];
        const wide = audience === 'company' && i >= 3;
        const base = `w-full rounded-xl border bg-canvas px-3.5 py-2.5 text-sm text-ink placeholder:text-muted/70 outline-none transition-colors duration-150 focus:border-accent focus:bg-surface ${error ? 'border-red-500' : 'border-line'}`;
        return <div key={f.name} className={wide ? 'sm:col-span-2' : ''} data-id="element-175" data-mp="2lg6tidfkcdut" data-mpi={i}>
              <label htmlFor={id} className="mb-1.5 block text-sm font-medium" data-id="element-176" data-mp="2gi58hmyku4gp">
                {f.label}
              </label>
              {f.type === 'select' ? <div className="relative" data-id="element-177" data-mp="3e7qfu67gi811">
                  <select id={id} value={values[f.name] ?? ''} onChange={e => handleChange(f.name, e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-err` : undefined} className={`${base} appearance-none pr-10 ${values[f.name] ? '' : 'text-muted'}`} data-id="element-178" data-mp="39614epgbnscf">
                    <option value="" disabled data-id="element-179" data-mp="3s4dpn9kicsod">
                      Pick one
                    </option>
                    {f.options?.map(o => <option key={o} value={o} data-id="element-180" data-mp="4b1ds9xmjpbr">
                        {o}
                      </option>)}
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-181" data-mp="d8lg7m783rny" />
                </div> : <input id={id} type={f.type} value={values[f.name] ?? ''} onChange={e => handleChange(f.name, e.target.value)} placeholder={f.placeholder} autoComplete={f.autoComplete} aria-invalid={!!error} aria-describedby={error ? `${id}-err` : undefined} className={base} data-id="element-182" data-mp="n6hqkbsc6il0" />}
              {error && <p id={`${id}-err`} className="mt-1 text-xs text-red-600" data-id="element-183" data-mp="1ay6me24d9p6p">
                  {error}
                </p>}
            </div>;
      })}
      </div>

      <div style={{
      position: 'absolute',
      left: '-9999px',
      width: '1px',
      height: '1px',
      overflow: 'hidden'
    }} data-id="element-184" data-mp="gao9vr1n7lcm">
        <label htmlFor={`${audience}-${HONEYPOT_FIELD}`} data-id="element-185" data-mp="uzugi02t8duj">Company website</label>
        <input id={`${audience}-${HONEYPOT_FIELD}`} type="text" name={HONEYPOT_FIELD} tabIndex={-1} autoComplete="off" aria-hidden="true" value={values[HONEYPOT_FIELD] ?? ''} onChange={e => handleChange(HONEYPOT_FIELD, e.target.value)} data-id="element-186" data-mp="q7j6g4ecvbo9" />
      </div>

      {status === 'error' && <p role="alert" className="mt-4 text-sm text-red-600" data-id="element-187" data-mp="18ykgbnh0krml">
          Something went wrong saving your details. Please try again, or email{' '}
          <a href="mailto:hello@localtask.cc" className="font-medium underline underline-offset-2" data-id="element-188" data-mp="3fptbl8yrasfq">
            hello@localtask.cc
          </a>
          .
        </p>}

      <button type="submit" disabled={submitting} className={`group mt-6 flex w-full items-center justify-center gap-2 rounded-xl py-3 font-medium transition-[transform,opacity] duration-150 ease-out active:scale-[0.98] disabled:opacity-70 ${audience === 'gigs' ? 'bg-accent text-accent-ink' : 'bg-ink text-canvas'}`} data-id="element-189" data-mp="1q7czlwv999ud">
        {submitting ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-190" data-mp="17gc097q83caa" />
            Signing you up
          </> : <>
            {copy.button}
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-191" data-mp="xjh4gwlc3pzp" />
          </>}
      </button>
      <p className="mt-3 text-center text-xs text-muted" data-id="element-192" data-mp="12kemn96cgk17">Free to join. No spam, ever.</p>
    </form>;
}